import { useQuery } from '@tanstack/react-query';
import { getBillingStripePromise, IS_STRIPE_TEST_MODE } from '@/services/billing/stripe/elements';

export type StripeLoadState = {
  /** Whether Stripe.js is still loading. */
  isLoading: boolean;
  /** Whether Stripe.js could not be loaded — usually an ad blocker holding back the script. */
  isError: boolean;
};

/**
 * Tracks whether Stripe.js loaded. `<Elements>` swallows the outcome, so an ad-blocked user sees plain
 * empty card fields, presses "Buy credits", and gets a PaymentIntent created server-side followed by a
 * generic failure and an unpaid invoice. So submit is gated on both the loading and the error state and
 * the copy says what to do about it; this hook is the state that makes that possible.
 *
 * `loadStripe` rejects when the script cannot be fetched and resolves `null` when there is no DOM to
 * inject it into, so both count as a load failure. Test mode never loads the real script, so it reports
 * loaded straight away.
 */
export function useStripeLoad(): StripeLoadState {
  const query = useQuery({
    queryKey: ['billing-stripe-js'],
    queryFn: async () => {
      const stripe = await getBillingStripePromise();
      if (stripe == null) {
        throw new Error('Stripe.js could not be loaded');
      }
      return true;
    },
    enabled: !IS_STRIPE_TEST_MODE,
    retry: false,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });

  if (IS_STRIPE_TEST_MODE) {
    return { isLoading: false, isError: false };
  }
  return { isLoading: query.isPending, isError: query.isError };
}
